import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAppContext } from '../../context/AppContext';
import { useMediaQuery } from '@mui/material';
import TourButton from '../../components/TourButton/TourButton';
import { getProfilePageSteps } from '../../tours/profilePage';
import {
    FaEnvelope,
    FaMapMarkerAlt,
    FaTicketAlt,
    FaCalendarAlt,
    FaSignOutAlt,
    FaGooglePlusG,
} from 'react-icons/fa';
import { SiMicrosoft, SiApple } from 'react-icons/si';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotification } from '../../components/notifications/useNotification';
import './Profile.css';

/**
 * ProfilePage component.
 *
 * Displays the authenticated dietist's personal information fetched from
 * the backend (`GET /dietists/:id`).  Shows:
 * - Profile avatar with initials fallback
 * - Full name / email
 * - Practice addresses
 * - Referral code (copyable)
 * - Member-since date
 * - Logout button
 *
 * @component
 * @returns {JSX.Element}
 */
const Profile = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { dietist, setDietist } = useAppContext();
    const { showSuccess, showError } = useNotification();
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [gcalLoading, setGcalLoading] = useState(false);
    const [mscalLoading, setMscalLoading] = useState(false);
    const [acalLoading, setAcalLoading] = useState(false);

    /* ── Handle ?gcal= query param after OAuth redirect ────────────── */
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const gcal = params.get('gcal');
        if (gcal === 'connected') {
            showSuccess(t('gcal-connected-success'));
            setProfile(prev => prev
                ? { ...prev, googleCalendar: { ...prev.googleCalendar, connected: true } }
                : prev
            );
        } else if (gcal === 'error') {
            showError(t('gcal-connected-error'));
        }
        if (gcal) {
            navigate('/profile', { replace: true });
        }

        const mscal = params.get('mscal');
        if (mscal === 'connected') {
            showSuccess(t('mscal-connected-success'));
            setProfile(prev => prev
                ? { ...prev, microsoftCalendar: { ...prev.microsoftCalendar, connected: true } }
                : prev
            );
        } else if (mscal === 'error') {
            showError(t('mscal-connected-error'));
        }
        if (mscal) {
            navigate('/profile', { replace: true });
        }

        const acal = params.get('acal');
        if (acal === 'connected') {
            showSuccess(t('acal-connected-success'));
            setProfile(prev => prev
                ? { ...prev, appleCalendar: { ...prev.appleCalendar, connected: true } }
                : prev
            );
        } else if (acal === 'error') {
            showError(t('acal-connected-error'));
        }
        if (acal) {
            navigate('/profile', { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Fetch full profile from backend ───────────────────────────── */
    useEffect(() => {
        if (!dietist?._id) return;

        let cancelled = false;
        const fetchProfile = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}`,
                );
                if (!cancelled) {
                    setProfile(res.data);
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || err.message);
                    setLoading(false);
                }
            }
        };
        fetchProfile();
        return () => { cancelled = true; };
    }, [dietist?._id]);

    /* ── Copy referral code to clipboard ───────────────────────────── */
    const handleCopyReferral = async () => {
        if (!profile?.referralCode) return;
        try {
            await navigator.clipboard.writeText(profile.referralCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard API may not be available */
        }
    };

    /* ── Logout ────────────────────────────────────────────────────── */
    const handleLogout = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`);
        } catch { /* best-effort */ }
        setDietist(null);
        localStorage.removeItem('dietist');
        navigate('/login');
    };
    /* ── Google Calendar ─────────────────────────────────────── */
    const handleGcalConnect = () => {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/google-calendar/auth`;
    };

    const handleGcalDisconnect = async () => {
        setGcalLoading(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/google-calendar/disconnect`,
                {},
                { withCredentials: true },
            );
            setProfile(prev => ({
                ...prev,
                googleCalendar: { ...prev?.googleCalendar, connected: false },
            }));
        } catch (err) {
            console.error('Failed to disconnect Google Calendar:', err.message);
        } finally {
            setGcalLoading(false);
        }
    };

    /* ── Microsoft Calendar ─────────────────────────────────────── */
    const handleMsCalConnect = () => {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/microsoft-calendar/auth`;
    };

    const handleMsCalDisconnect = async () => {
        setMscalLoading(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/microsoft-calendar/disconnect`,
                {},
                { withCredentials: true },
            );
            setProfile(prev => ({
                ...prev,
                microsoftCalendar: { ...prev?.microsoftCalendar, connected: false },
            }));
        } catch (err) {
            console.error('Failed to disconnect Microsoft Calendar:', err.message);
        } finally {
            setMscalLoading(false);
        }
    };

    /* ── Apple Calendar (Nylas) ─────────────────────────────────────── */
    const handleAcalConnect = () => {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/nylas-calendar/auth`;
    };

    const handleAcalDisconnect = async () => {
        setAcalLoading(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/nylas-calendar/disconnect`,
                {},
                { withCredentials: true },
            );
            setProfile(prev => ({
                ...prev,
                appleCalendar: { ...prev?.appleCalendar, connected: false },
            }));
        } catch (err) {
            console.error('Failed to disconnect Apple Calendar:', err.message);
        } finally {
            setAcalLoading(false);
        }
    };

    /* ── Initials fallback ─────────────────────────────────────────── */
    const initials = profile
        ? `${profile.fullName?.[0] ?? ''}`.toUpperCase()
        : (dietist
            ? `${dietist.name?.[0] ?? ''}${dietist.surname?.[0] ?? ''}`.toUpperCase()
            : '');

    /* ── Loading / error states ────────────────────────────────────── */
    if (loading) {
        return (
            <div className="pp">
                <div className="pp-loading">{t('loading')}...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pp">
                <div className="pp-error">{error}</div>
            </div>
        );
    }

    /* ── Derived display values ────────────────────────────────────── */
    const displayName = profile?.fullName || `${dietist?.name ?? ''} ${dietist?.surname ?? ''}`.trim() || '—';
    const memberSince = profile?.createdAt
        ? new Date(profile.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
        : '—';

    return (
        <div className="pp">
            <div className="pp-card" data-tour="pp-card">

                {/* ── Header / avatar section ── */}
                <div className="pp-header">
                    <div className='pp-tutorial-btn'>
                        <TourButton steps={getProfilePageSteps(t)} iconOnly={isMobile} />
                    </div>
                    <div className="pp-avatar">
                        {profile?.profileImage
                            ? <img src={profile.profileImage} alt={initials} className="pp-avatar-img" />
                            : <span className="pp-avatar-initials">{initials}</span>
                        }
                    </div>
                    <h2 className="pp-name">{displayName}</h2>
                    {profile?.email && (
                        <span className="pp-email">{profile.email}</span>
                    )}
                </div>

                {/* ── Info rows ── */}
                <div className="pp-info">
                    {/* Email */}
                    <div className="pp-row">
                        <FaEnvelope className="pp-row-icon" />
                        <div className="pp-row-content">
                            <span className="pp-row-label">{t('Email')}</span>
                            <span className="pp-row-value">{profile?.email || '—'}</span>
                        </div>
                    </div>

                    {/* Practice addresses */}
                    {profile?.addresses?.length > 0 && (
                        <div className="pp-row pp-row--top">
                            <FaMapMarkerAlt className="pp-row-icon" />
                            <div className="pp-row-content">
                                <span className="pp-row-label">{t('pp-addresses')}</span>
                                {profile.addresses.map((addr, i) => (
                                    <span key={i} className="pp-row-value">{addr}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Referral code */}
                    <div className="pp-row" data-tour="pp-referral">
                        <FaTicketAlt className="pp-row-icon" />
                        <div className="pp-row-content">
                            <span className="pp-row-label">{t('pp-referral-code')}</span>
                            {profile?.referralCode ? (
                                <button
                                    className="pp-referral-btn"
                                    onClick={handleCopyReferral}
                                    title={t('pp-copy-referral')}
                                >
                                    <code className="pp-referral-code">{profile.referralCode}</code>
                                    <span className="pp-copy-hint">
                                        {copied ? t('pp-copied') : t('pp-copy-referral')}
                                    </span>
                                </button>
                            ) : (
                                <span className="pp-row-value pp-row-value--muted">{t('pp-no-referral')}</span>
                            )}
                        </div>
                    </div>

                    {/* Member since */}
                    <div className="pp-row">
                        <FaCalendarAlt className="pp-row-icon" />
                        <div className="pp-row-content">
                            <span className="pp-row-label">{t('pp-member-since')}</span>
                            <span className="pp-row-value">{memberSince}</span>
                        </div>
                    </div>

                    {/* Google Calendar */}
                    <div className="pp-row" data-tour="pp-gcal">
                        <FaGooglePlusG className="pp-row-icon" />
                        <div className="pp-row-content">
                            <span className="pp-row-label">{t('pp-gcal-label')}</span>
                            {profile?.googleCalendar?.connected ? (
                                <div className="pp-gcal-connected">
                                    <span className="pp-gcal-status pp-gcal-status--on">
                                        {t('pp-gcal-connected')}
                                    </span>
                                    <button
                                        className="pp-gcal-disconnect-btn"
                                        onClick={handleGcalDisconnect}
                                        disabled={gcalLoading}
                                    >
                                        {gcalLoading ? t('loading') : t('pp-gcal-disconnect')}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="pp-gcal-connect-btn"
                                    onClick={handleGcalConnect}
                                >
                                    {t('gcal-connect')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Microsoft Calendar */}
                    <div className="pp-row" data-tour="pp-mscal">
                        <SiMicrosoft className="pp-row-icon" />
                        <div className="pp-row-content">
                            <span className="pp-row-label">{t('pp-mscal-label')}</span>
                            {profile?.microsoftCalendar?.connected ? (
                                <div className="pp-gcal-connected">
                                    <span className="pp-gcal-status pp-gcal-status--on">
                                        {t('pp-mscal-connected')}
                                    </span>
                                    <button
                                        className="pp-gcal-disconnect-btn"
                                        onClick={handleMsCalDisconnect}
                                        disabled={mscalLoading}
                                    >
                                        {mscalLoading ? t('loading') : t('pp-mscal-disconnect')}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="pp-gcal-connect-btn"
                                    onClick={handleMsCalConnect}
                                >
                                    {t('mscal-connect')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Apple Calendar (via Nylas) */}
                    {/*}
                    <div className="pp-row" data-tour="pp-acal">
                        <SiApple className="pp-row-icon" />
                        <div className="pp-row-content">
                            <span className="pp-row-label">{t('pp-acal-label')}</span>
                            {profile?.appleCalendar?.connected ? (
                                <div className="pp-gcal-connected">
                                    <span className="pp-gcal-status pp-gcal-status--on">
                                        {t('pp-acal-connected')}
                                    </span>
                                    <button
                                        className="pp-gcal-disconnect-btn"
                                        onClick={handleAcalDisconnect}
                                        disabled={acalLoading}
                                    >
                                        {acalLoading ? t('loading') : t('pp-acal-disconnect')}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="pp-gcal-connect-btn"
                                    onClick={handleAcalConnect}
                                >
                                    {t('acal-connect')}
                                </button>
                            )}
                        </div>
                    </div>
                    {*/}
                </div>

                {/* ── Logout button ── */}
                <button className="pp-logout-btn" onClick={handleLogout}>
                    <FaSignOutAlt className="pp-logout-icon" />
                    {t('pp-logout')}
                </button>
            </div>
        </div>
    );
};

export default Profile;
